import cookie from 'cookie';
import actionTypes from 'Modules/auth/actions/types';
import {
  loginUserFailure,
  loginUserStart,
  loginUserSuccess,
  subscribeNewsletterFailure,
  subscribeNewsletterRequest,
  subscribeNewsletterSuccess,
} from 'Modules/auth/actions';

import { browser } from '@openclassrooms/utils';
import { isFromHomePage, getPathName, ROUTES } from '@openclassrooms/routing';
import {
  removeCachedCurrentUser,
  setCachedCurrentUser,
} from '@openclassrooms/data-connect-web/users';
import { ajaxPost } from 'Modules/ajax';
import sdk from 'Modules/api/services/sdk';
import scopes from 'Modules/api/constants/scopes';
import { getShouldAskForBenefits } from 'Modules/currentUser/services';

const NO_WELCOME_QUESTIONS = ['/application', '/subscribe', '/mp', '/vap'];

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function initializeSdkClient() {
  const cookies = cookie.parse(browser.document.cookie);
  const COOKIES_ACCESS_TOKEN_KEY = 'access_token';
  const COOKIES_EXPIRES_IN_KEY = 'access_token_expires_in';

  sdk.switchToConnectedClient();

  sdk.authClient.setOauth2Tokens({
    accessToken: cookies[COOKIES_ACCESS_TOKEN_KEY],
    expiresIn: cookies[COOKIES_EXPIRES_IN_KEY],
    grantType: 'implicit',
    scopes: scopes.CONNECTED,
  });
}

const signInMiddleware = (store) => (next) => async (action) => {
  const { type, payload } = action;

  const redirectAfterLogin = async () => {
    await removeCachedCurrentUser();

    const redirectLink = isFromHomePage(payload?.redirectLink)
      ? getPathName(ROUTES.DASHBOARD.INDEX)
      : payload?.redirectLink;

    const isInvitationContext = payload.authContext?.id === 'invitation';

    const canDisplayWelcome =
      !isInvitationContext &&
      !payload.hasNoWelcomeQuestions &&
      !NO_WELCOME_QUESTIONS.find((urlPart) => payload?.redirectLink?.includes(urlPart));

    if (canDisplayWelcome) {
      const welcomePageUrl = `${getPathName(
        ROUTES.WELCOME.INDEX
      )}?redirectLink=${encodeURIComponent(redirectLink)}`;
      if (payload.isFirstTime) {
        browser.window.location.href = welcomePageUrl;

        return;
      }

      try {
        initializeSdkClient();
        const { client } = sdk;
        const { body: currentUser } = await client.helpers.user.me.get();

        await setCachedCurrentUser(currentUser);

        const userId = currentUser.id;
        const shouldAskForBenefits = await getShouldAskForBenefits(userId);
        if (shouldAskForBenefits) {
          browser.window.location.href = `${welcomePageUrl}&steps=benefits`;
          return;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    }

    browser.window.location.href = redirectLink;
  };

  const subscribeToNewsletter = async () => {
    store.dispatch(subscribeNewsletterRequest());
    try {
      initializeSdkClient();
      await sdk.client
        .createPutRequest(`/users/${payload.userId}/communications`)
        .setBodyParams({ newsletter: true })
        .execute();

      store.dispatch(subscribeNewsletterSuccess());
    } catch (error) {
      store.dispatch(subscribeNewsletterFailure(error));
    }
  };

  if (type === actionTypes.POST_LOGIN_USER_TRIGGER) {
    store.dispatch(loginUserStart());
    try {
      const formData = {
        _username: payload._username, // eslint-disable-line no-underscore-dangle
        _password: payload._password, // eslint-disable-line no-underscore-dangle
        _remember_me: payload.rememberMe ? 1 : 0,
        state: payload.csrfToken,
      };

      const queryString = payload.isFirstTime ? '?type=first' : '';

      const login = await ajaxPost(`/login_check${queryString}`, {
        formData,
        headers: {
          Accept: '*/*',
        },
        noLog: ({ status }) => status === 401,
      });

      if (login.status === 200) {
        store.dispatch(loginUserSuccess());

        if (payload.subscribeNewsletter) {
          await wait(1200);
          await subscribeToNewsletter();
        }

        redirectAfterLogin();
      } else {
        store.dispatch(loginUserFailure(login.data));
      }
    } catch (error) {
      store.dispatch(loginUserFailure(error.responseJSON || error));
    }
  }

  return next(action);
};

export default signInMiddleware;
